// Wikimedia Commons image search, called straight from the browser.
// Commons needs no API key and allows anonymous CORS (`origin=*`), so unlike
// Unsplash/GIFs there is no general-api proxy. We use `fetch` with
// `credentials: 'omit'` on purpose: the app's Vue.http interceptors would attach
// our auth headers, which must never go to a third party.

const COMMONS_API_URL = 'https://commons.wikimedia.org/w/api.php'

// Wikimedia asks browser clients to identify themselves with this header
// (browsers do not let JS set User-Agent).
const API_USER_AGENT = 'AhaSlides/1.0 (https://ahaslides.com)'

export const PAGE_SIZE = 30

// Must be a Wikimedia standard thumbnail bucket; other widths get rate-limited.
const PREVIEW_WIDTH = 500
// Same cap we use for Unsplash (AHA-8204): larger images make the cropper slow.
const MAX_FULL_WIDTH = 1920

// GIFs would be sent down the GIF-provider path in UploadImage.v2 (it switches
// on the file extension), and TIFF/SVG originals can't be cropped in the browser.
const SUPPORTED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp']
const SEARCH_FILTERS = 'filetype:bitmap -filemime:gif -filemime:tiff'

// Only licenses a presenter can reuse with a simple credit line. GFDL-only files
// are dropped because GFDL requires shipping the full license text with the copy.
const REUSABLE_LICENSE_PATTERN = /^(CC0|CC BY|Public domain|PD)\b/i
// "CC BY" also prefixes non-commercial / no-derivatives variants (CC BY-NC, CC BY-ND,
// CC BY-NC-SA ...): those forbid our use (commercial product, cropping) and are dropped.
const RESTRICTED_LICENSE_PATTERN = /(^|[\s-])(NC|ND)([\s-]|$)/i

const isReusableLicense = (license) => REUSABLE_LICENSE_PATTERN.test(license)
  && !RESTRICTED_LICENSE_PATTERN.test(license)

// `Attribution` is the credit text the author asked for, when they set one.
// `Restrictions` lists non-copyright limits Commons tags on a file (trademarked logo,
// identifiable person, insignia...): a free license does not clear those, so we drop them.
const METADATA_FIELDS = ['Artist', 'Attribution', 'LicenseShortName', 'LicenseUrl', 'AttributionRequired',
  'Restrictions']

// Some authors fill the Artist / Attribution fields with a whole paragraph of reuse
// terms; longer than this is not a usable credit line.
const MAX_AUTHOR_LENGTH = 80
const MAX_ATTRIBUTION_LENGTH = 120
const SOURCE_NAME = 'Wikimedia Commons'

const stripTrackingParams = (url = '') => url.split('?')[0]

const parseHtml = (html) => new DOMParser().parseFromString(html, 'text/html').body

// Commons templates add hidden copies of the text (e.g. "Unknown author" twice).
const visibleText = (element) => {
  element.querySelectorAll('[style*="display: none"], [style*="display:none"]').forEach((hidden) => hidden.remove())
  return (element.textContent || '').replace(/\s+/g, ' ').trim()
}

const htmlToText = (html = '') => (html ? visibleText(parseHtml(html)) : '')

const readAuthor = (artistHtml = '') => {
  if (!artistHtml) return ''
  const body = parseHtml(artistHtml)
  const text = visibleText(body)
  if (text.length <= MAX_AUTHOR_LENGTH) return text
  const userLink = body.querySelector('a[href*="/wiki/User:"]')
  return userLink ? visibleText(userLink) : ''
}

const readAttribution = (attributionHtml) => {
  const text = htmlToText(attributionHtml)
  return text.length <= MAX_ATTRIBUTION_LENGTH ? text : ''
}

const fileTitle = (pageTitle = '') => pageTitle.replace(/^File:/, '').replace(/\.[^.]+$/, '')

const metadataValue = (extmetadata, key) => (extmetadata && extmetadata[key] ? extmetadata[key].value : '')

export const buildSearchUrl = ({ query, offset = 0 }) => {
  const params = new URLSearchParams({
    action: 'query',
    format: 'json',
    formatversion: '2',
    origin: '*',
    generator: 'search',
    gsrsearch: `${query} ${SEARCH_FILTERS}`,
    gsrnamespace: '6', // File: namespace
    gsrlimit: String(PAGE_SIZE),
    gsroffset: String(offset),
    prop: 'imageinfo',
    iiprop: 'url|size|mime|extmetadata',
    iiurlwidth: String(PREVIEW_WIDTH),
    iiextmetadatafilter: METADATA_FIELDS.join('|'),
    iiextmetadatalanguage: 'en',
  })
  return `${COMMONS_API_URL}?${params.toString()}`
}

// Thumb URLs end in `/<width>px-<file name>`; only that last segment may change
// (a file name can itself start with "500px-").
const PREVIEW_SEGMENT_PATTERN = new RegExp(`/${PREVIEW_WIDTH}px-([^/]+)$`)

const buildFullUrl = (info, previewUrl) => {
  if (info.width > MAX_FULL_WIDTH && PREVIEW_SEGMENT_PATTERN.test(previewUrl)) {
    return previewUrl.replace(PREVIEW_SEGMENT_PATTERN, `/${MAX_FULL_WIDTH}px-$1`)
  }
  return stripTrackingParams(info.url)
}

// Format recommended by Commons (commons.wikimedia.org/wiki/Commons:Credit_line). The
// author's own requested credit wins over the plain name: Commons asks reusers to follow it.
// Not translated: names, "Wikimedia Commons" and license codes read the same everywhere.
export const formatCreditLine = ({ author, attribution, license }) => {
  const credit = attribution || author
  // An author's own credit text may already name the source or the license; do not repeat them.
  const source = credit && credit.includes(SOURCE_NAME) ? '' : SOURCE_NAME
  const shownLicense = credit && license && credit.includes(license) ? '' : license
  return [credit, source, shownLicense].filter(Boolean).join(' / ')
}

export const normalizeCommonsPage = (page) => {
  const info = page && page.imageinfo && page.imageinfo[0]
  if (!info || !SUPPORTED_MIME_TYPES.includes(info.mime)) return null

  const license = metadataValue(info.extmetadata, 'LicenseShortName')
  if (!isReusableLicense(license)) return null
  if (metadataValue(info.extmetadata, 'Restrictions')) return null

  const previewUrl = stripTrackingParams(info.thumburl || info.url)
  return {
    id: page.pageid,
    title: fileTitle(page.title),
    previewUrl,
    fullUrl: buildFullUrl(info, previewUrl),
    width: info.width,
    height: info.height,
    author: readAuthor(metadataValue(info.extmetadata, 'Artist')),
    attribution: readAttribution(metadataValue(info.extmetadata, 'Attribution')),
    license,
    licenseUrl: metadataValue(info.extmetadata, 'LicenseUrl'),
    attributionRequired: metadataValue(info.extmetadata, 'AttributionRequired') !== 'false',
    sourceUrl: info.descriptionurl,
  }
}

const searchImages = async ({ query, offset = 0, fetchFn = fetch }) => {
  const trimmedQuery = (query || '').trim()
  if (!trimmedQuery) return { items: [], nextOffset: null }

  const response = await fetchFn(buildSearchUrl({ query: trimmedQuery, offset }), {
    credentials: 'omit',
    headers: { 'Api-User-Agent': API_USER_AGENT },
  })
  if (!response.ok) {
    throw new Error(`Wikimedia Commons search failed: HTTP ${response.status}`)
  }

  const body = await response.json()
  if (body.error) {
    throw new Error(`Wikimedia Commons search failed: ${body.error.code} - ${body.error.info}`)
  }

  // generator=search returns pages unordered; `index` is the search rank.
  const pages = ((body.query && body.query.pages) || []).slice().sort((a, b) => a.index - b.index)
  return {
    items: pages.map(normalizeCommonsPage).filter(Boolean),
    // `continue` can also carry only an imageinfo continuation; that is not a next page.
    nextOffset: body.continue && body.continue.gsroffset != null ? body.continue.gsroffset : null,
  }
}

export default {
  searchImages,
}
